// apps/client/src/pages/admin/dashboard/campaigns/page.tsx

import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@reactive-resume/ui";
import {
  BellSimpleIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  ClockIcon,
  DeviceMobileIcon,
  EnvelopeSimpleIcon,
  EyeIcon,
  MegaphoneIcon,
  PaperPlaneRightIcon,
  PaperPlaneTiltIcon,
  PencilSimpleLineIcon,
  PlusIcon,
  ProhibitIcon,
  TrashIcon,
  UploadSimpleIcon,
  UsersIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { toast } from "sonner";

import { KpiCard } from "../_components/kpi-card";
import { Column, DataTable } from "../_components/datatables";
import { EmailBlocksEditor } from "./_components/email-blocks-editor";
import {
  useCampaign,
  useCampaignRecipients,
  useCampaigns,
  useCancelCampaign,
  useContactDeliveries,
  useContactStats,
  useCreateCampaign,
  useDeleteCampaign,
  useSendCampaign,
  useSendTestCampaign,
  useSegmentCount,
  useSegmentCriteria,
  useSegmentPreview,
  type Campaign,
  type CampaignChannel,
  type CampaignPriority,
  type CampaignStatus,
  type EmailBlocks,
  type Segment,
} from "@/client/services/campaigns/campaigns";
import { useProvisionBatches } from "@/client/services/campaigns/provisioning";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CONSOLE CAMPAGNES (admin)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Liste à statuts + progression live (socket `campaign:update`), composer en
 *  4 étapes (contenu → ciblage avec compteur d'audience live → canaux/
 *  priorité → planification), envoi test, monitoring destinataires/erreurs,
 *  annulation. Dialogues inline (état local) — pas de deep-link requis.
 * ─────────────────────────────────────────────────────────────────────────────
 */

const CHANNEL_META: Record<CampaignChannel, { label: string; icon: React.ReactNode }> = {
  inApp: { label: "In-app", icon: <BellSimpleIcon size={14} /> },
  push: { label: "Push", icon: <DeviceMobileIcon size={14} /> },
  email: { label: "E-mail", icon: <EnvelopeSimpleIcon size={14} /> },
};

const STATUS_META: Record<CampaignStatus, { label: string; className: string }> = {
  draft: { label: "Brouillon", className: "bg-slate-500/10 text-slate-600 " },
  scheduled: { label: "Programmée", className: "bg-sky-500/10 text-sky-600 " },
  sending: {
    label: "Envoi en cours",
    className: "bg-amber-500/10 text-amber-600 ",
  },
  completed: {
    label: "Terminée",
    className: "bg-emerald-500/10 text-emerald-600 ",
  },
  cancelled: { label: "Annulée", className: "bg-zinc-500/10 text-zinc-600 " },
  failed: { label: "Échec", className: "bg-red-500/10 text-red-600 " },
};

const PRIORITY_META: Record<CampaignPriority, { label: string; className: string }> = {
  low: { label: "Basse", className: "bg-admin-surface-elevated text-admin-text-secondary" },
  normal: { label: "Normale", className: "bg-sky-500/10 text-sky-600 " },
  high: { label: "Haute", className: "bg-amber-500/10 text-amber-600 " },
};

const fmtDateTime = (iso?: string | null) =>
  iso
    ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(
        new Date(iso),
      )
    : "—";

const parseIdList = (raw: string) =>
  raw
    .split(/[\n,;\s]+/)
    .map((s) => s.trim())
    .filter(Boolean);

/** ── Page principale ─────────────────────────────────────────────────────── */

export const CampaignsAdminPage = () => {
  const [status, setStatus] = useState<"all" | CampaignStatus>("all");
  const [search, setSearch] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  const { campaigns, loading } = useCampaigns({
    status: status === "all" ? undefined : status,
    search: search.trim() || undefined,
  });

  const stats = useMemo(() => {
    const sending = campaigns.filter(
      (c) => c.status === "sending" || c.status === "scheduled",
    ).length;
    const drafts = campaigns.filter((c) => c.status === "draft").length;
    const audience = campaigns
      .filter((c) => c.status === "completed")
      .reduce((sum, c) => sum + (c.totalAudience ?? 0), 0);
    return { total: campaigns.length, sending, drafts, audience };
  }, [campaigns]);

  const columns: Column<Campaign>[] = [
    {
      key: "title",
      header: "Campagne",
      render: (c) => (
        <div className="max-w-xs">
          <p className="truncate font-semibold text-admin-text-primary">{c.title}</p>
          {c.body && <p className="truncate text-xs text-admin-text-muted">{c.body}</p>}
        </div>
      ),
    },
    {
      key: "channels",
      header: "Canaux",
      render: (c) => (
        <div className="flex flex-wrap gap-1">
          {c.channels.map((ch) => (
            <Badge
              key={ch}
              className="gap-1 border-0 bg-admin-surface-hover text-xs text-admin-text-secondary"
            >
              {CHANNEL_META[ch].icon}
              {CHANNEL_META[ch].label}
            </Badge>
          ))}
        </div>
      ),
    },
    {
      key: "status",
      header: "Statut",
      render: (c) => (
        <Badge className={cn("border-0", STATUS_META[c.status].className)}>
          {c.status === "sending" && <CircleNotchIcon size={12} className="mr-1 animate-spin" />}
          {STATUS_META[c.status].label}
        </Badge>
      ),
    },
    {
      key: "totalAudience",
      header: "Audience",
      align: "right",
      render: (c) => (c.totalAudience != null ? c.totalAudience.toLocaleString("fr-FR") : "—"),
    },
    {
      key: "processedCount",
      header: "Progression",
      render: (c) => {
        if (c.status === "completed") {
          return (
            <span className="inline-flex items-center gap-1 text-emerald-600">
              <CheckCircleIcon size={14} /> {c.processedCount ?? c.totalAudience ?? 0} traités
            </span>
          );
        }
        if (c.status === "sending" && c.totalAudience) {
          const done = c.processedCount ?? 0;
          const pct = Math.min(100, Math.round((done / c.totalAudience) * 100));
          return (
            <div className="w-32">
              <div className="h-1.5 overflow-hidden rounded-full bg-admin-surface-elevated">
                <div
                  className="h-full rounded-full bg-amber-500 transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-admin-text-muted">
                {done}/{c.totalAudience} · {pct}%
              </span>
            </div>
          );
        }
        if (c.status === "scheduled") {
          return (
            <span className="inline-flex items-center gap-1 text-sky-600">
              <ClockIcon size={14} /> {fmtDateTime(c.scheduledAt)}
            </span>
          );
        }
        if (c.status === "failed" && c.lastError) {
          return (
            <span
              className="inline-flex max-w-48 items-center gap-1 truncate text-red-600"
              title={c.lastError}
            >
              <WarningCircleIcon size={14} /> {c.lastError}
            </span>
          );
        }
        return <span className="text-admin-text-muted">—</span>;
      },
    },
    {
      key: "createdAt",
      header: "Créée le",
      align: "right",
      render: (c) => <span className="text-sm">{fmtDateTime(c.createdAt)}</span>,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* En-tête */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-admin-text-primary">Campagnes</h1>
          <p className="text-sm text-admin-text-muted">
            Notifications groupées in-app, push et e-mail — ciblage par segment.
          </p>
        </div>
        <Button onClick={() => setComposerOpen(true)} className="gap-2">
          <PlusIcon size={16} /> Nouvelle campagne
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={<MegaphoneIcon size={20} />}
          label="Campagnes"
          value={stats.total}
          tone="violet"
        />
        <KpiCard
          icon={<PaperPlaneTiltIcon size={20} />}
          label="En cours / programmées"
          value={stats.sending}
          tone={stats.sending > 0 ? "amber" : "neutral"}
        />
        <KpiCard
          icon={<UsersIcon size={20} />}
          label="Utilisateurs touchés"
          value={stats.audience.toLocaleString("fr-FR")}
          tone="emerald"
        />
        <KpiCard
          icon={<PencilSimpleLineIcon size={20} />}
          label="Brouillons"
          value={stats.drafts}
          tone={stats.drafts > 0 ? "sky" : "neutral"}
        />
      </div>

      {/* Filtres */}
      <div className="flex flex-wrap items-center gap-3">
        <Input
          placeholder="Rechercher une campagne…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full max-w-xs border-admin-border bg-admin-surface"
        />
        <Select value={status} onValueChange={(v) => setStatus(v as typeof status)}>
          <SelectTrigger className="w-52 border-admin-border bg-admin-surface">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {(Object.keys(STATUS_META) as CampaignStatus[]).map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_META[s].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Liste */}
      <DataTable
        columns={columns}
        data={campaigns}
        loading={loading}
        onRowClick={(c) => setDetailId(c.id)}
        pageSize={10}
        emptyState={
          <div className="flex flex-col items-center gap-2">
            <MegaphoneIcon size={32} className="text-admin-text-muted" />
            <p>
              Aucune campagne
              {status !== "all" || search ? " pour ces filtres" : " — créez la première"}.
            </p>
          </div>
        }
      />

      {composerOpen && <CampaignComposer onClose={() => setComposerOpen(false)} />}
      {detailId && <CampaignDetailDialog campaignId={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
};

/** ── Composer 5 étapes ──────────────────────────────────────────────────── */

const STEPS = ["Contenu", "E-mail", "Audience", "Canaux", "Planification"] as const;

const CampaignComposer = ({ onClose }: { onClose: () => void }) => {
  const [step, setStep] = useState(0);
  // Étape 1 — contenu
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  // Étape 2 — e-mail riche
  const [emailSubject, setEmailSubject] = useState("");
  const [emailPreheader, setEmailPreheader] = useState("");
  const [emailBlocks, setEmailBlocks] = useState<EmailBlocks>({});
  // Étape 3 — ciblage
  const { criteria } = useSegmentCriteria();
  const [roles, setRoles] = useState<(string | number)[]>([]);
  const [bools, setBools] = useState<Record<string, boolean | undefined>>({});
  const [nums, setNums] = useState<Record<string, string>>({});
  const [excludeRaw, setExcludeRaw] = useState("");
  const [includePlatformUsers, setIncludePlatformUsers] = useState(true);
  const [provisionBatchId, setProvisionBatchId] = useState("");
  const [includeContacts, setIncludeContacts] = useState(false);
  const { batches } = useProvisionBatches();
  const { stats: contactStats } = useContactStats();
  // Étape 4 — canaux
  const [channels, setChannels] = useState<CampaignChannel[]>(["inApp"]);
  const [priority, setPriority] = useState<CampaignPriority>("normal");
  // Étape 5 — planification
  const [sendMode, setSendMode] = useState<"now" | "schedule">("now");
  const [scheduledAt, setScheduledAt] = useState("");

  const roleCriteria = criteria.find((c) => c.key === "roles" && c.options?.length);
  const excludeCriteria = criteria.find((c) => c.key === "excludeUserIds");
  const selectedBatch = batches.find((b) => b.id === provisionBatchId);

  const segment: Segment | null = useMemo(() => {
    const s: Record<string, unknown> = {};
    if (roles.length) s.roles = roles;
    for (const [k, v] of Object.entries(bools)) if (typeof v === "boolean") s[k] = v;
    for (const [k, v] of Object.entries(nums)) {
      const n = Number(v);
      if (v !== "" && Number.isFinite(n)) s[k] = n;
    }
    const exclusions = parseIdList(excludeRaw);
    if (exclusions.length) s.excludeUserIds = exclusions;
    return Object.keys(s).length ? s : null;
  }, [roles, bools, nums, excludeRaw]);

  // Compteur d'audience live — debounce 400 ms pour éviter de marteler l'API.
  const [debouncedSegment, setDebouncedSegment] = useState<Segment | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSegment(segment), 400);
    return () => clearTimeout(t);
  }, [segment]);
  const countQuery = useSegmentCount(includePlatformUsers ? debouncedSegment : null);

  const preview = useSegmentPreview();
  const [previewSample, setPreviewSample] = useState<
    { id: string; name: string; email: string }[] | null
  >(null);
  const [previewTotal, setPreviewTotal] = useState<number | null>(null);

  const create = useCreateCampaign();

  const stepValid = useMemo(() => {
    if (step === 0) return title.trim().length > 0 && body.trim().length > 0;
    if (step === 1) return true; // e-mail : tout est optionnel (fallback = body)
    if (step === 2) return includePlatformUsers || Boolean(provisionBatchId) || includeContacts;
    if (step === 3) return channels.length > 0;
    if (step === 4)
      return (
        sendMode === "now" || (scheduledAt !== "" && new Date(scheduledAt).getTime() > Date.now())
      );
    return true;
  }, [
    step,
    title,
    body,
    channels,
    sendMode,
    scheduledAt,
    includePlatformUsers,
    provisionBatchId,
    includeContacts,
  ]);

  const toggleRole = (value: string | number) =>
    setRoles((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const toggleChannel = (ch: CampaignChannel) =>
    setChannels((prev) => (prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch]));

  const runPreview = async () => {
    try {
      const result = await preview.mutateAsync(segment ?? {});
      setPreviewSample(result.sample ?? []);
      setPreviewTotal(result.total);
    } catch {
      toast.error("Impossible de charger l'aperçu du segment");
    }
  };

  const submit = async () => {
    try {
      const hasRichEmail = Boolean(
        emailBlocks.intro ||
          (emailBlocks.sections?.length ?? 0) > 0 ||
          emailBlocks.heroImageUrl ||
          emailBlocks.ctaUrl,
      );
      await create.mutateAsync({
        title: title.trim(),
        body: body.trim() || undefined,
        link: link.trim() || undefined,
        segment: includePlatformUsers ? (segment ?? undefined) : undefined,
        channels,
        priority,
        scheduledAt:
          sendMode === "schedule" && scheduledAt ? new Date(scheduledAt).toISOString() : undefined,
        emailSubject: emailSubject.trim() || undefined,
        emailPreheader: emailPreheader.trim() || undefined,
        emailBlocks: hasRichEmail ? emailBlocks : undefined,
        includePlatformUsers,
        provisionBatchId: provisionBatchId || undefined,
        includeContacts,
      });
      toast.success(
        sendMode === "schedule"
          ? `Campagne programmée pour le ${fmtDateTime(new Date(scheduledAt).toISOString())}`
          : "Campagne créée — l'envoi démarre",
      );
      onClose();
    } catch {
      toast.error("Échec de la création de la campagne");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto border-admin-border bg-admin-surface">
        <DialogHeader>
          <DialogTitle className="text-admin-text-primary">Nouvelle campagne</DialogTitle>
          <DialogDescription className="text-admin-text-muted">
            Diffusez une annonce à un segment d'utilisateurs via les canaux choisis.
          </DialogDescription>
        </DialogHeader>

        {/* Indicateur d'étapes */}
        <div className="flex items-center gap-2">
          {STEPS.map((label, i) => (
            <button
              key={label}
              type="button"
              onClick={() => i < step && setStep(i)}
              className={cn(
                "flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                i === step
                  ? "bg-admin-primary text-admin-primary-foreground"
                  : i < step
                    ? "bg-emerald-500/10 text-emerald-600"
                    : "bg-admin-surface-elevated text-admin-text-muted",
              )}
            >
              {i < step ? <CheckCircleIcon size={12} /> : <span>{i + 1}.</span>}
              {label}
            </button>
          ))}
        </div>

        <Separator className="bg-admin-border" />

        {/* Étape 1 — Contenu */}
        {step === 0 && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-admin-text-secondary">Titre *</label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ex. Nouveautés de la plateforme"
                className="border-admin-border bg-admin-surface-elevated"
                maxLength={120}
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-admin-text-secondary">Message *</label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Contenu de la notification…"
                rows={6}
                maxLength={2000}
                className="w-full rounded-md border border-admin-border bg-admin-surface-elevated p-3 text-sm text-admin-text-primary outline-none placeholder:text-admin-text-muted focus:ring-1 focus:ring-admin-primary"
              />
              <span className="text-right text-xs text-admin-text-muted">{body.length}/2000</span>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-admin-text-secondary">
                Lien (optionnel)
              </label>
              <Input
                value={link}
                onChange={(e) => setLink(e.target.value)}
                placeholder="/dashboard ou https://…"
                className="border-admin-border bg-admin-surface-elevated"
              />
            </div>
          </div>
        )}

        {/* Étape 2 — E-mail riche */}
        {step === 1 && (
          <EmailBlocksEditor
            subject={emailSubject}
            onSubjectChange={setEmailSubject}
            preheader={emailPreheader}
            onPreheaderChange={setEmailPreheader}
            blocks={emailBlocks}
            onBlocksChange={setEmailBlocks}
            fallbackBody={body}
            link={link}
            title={title}
          />
        )}

        {/* Étape 3 — Ciblage */}
        {step === 2 && (
          <div className="flex flex-col gap-4">
            {/* Sources d'audience */}
            <div className="rounded-xl-admin border border-admin-border bg-admin-surface-elevated p-3">
              <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-admin-text-primary">
                <UsersIcon size={14} /> Sources d'audience (combinables)
              </p>
              <div className="flex flex-col gap-2">
                <label className="flex cursor-pointer items-center justify-between gap-3">
                  <span>
                    <span className="text-sm font-medium text-admin-text-secondary">
                      Comptes de la plateforme
                    </span>
                    <span className="ml-2 text-xs text-admin-text-muted">
                      segment ci-dessous (défaut : tous les actifs)
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={includePlatformUsers}
                    onChange={(e) => setIncludePlatformUsers(e.target.checked)}
                    className="h-4 w-4 accent-[#472cb3]"
                  />
                </label>
                <label className="flex cursor-pointer items-center justify-between gap-3">
                  <span>
                    <span className="text-sm font-medium text-admin-text-secondary">
                      Comptes migrés (lot d'import)
                    </span>
                    <span className="ml-2 text-xs text-admin-text-muted">
                      comptes provisionnés = comptes réels
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={provisionBatchId !== ""}
                    onChange={(e) =>
                      setProvisionBatchId(e.target.checked ? (batches[0]?.id ?? "") : "")
                    }
                    disabled={batches.length === 0}
                    className="h-4 w-4 accent-[#472cb3]"
                  />
                </label>
                {provisionBatchId !== "" && (
                  <Select value={provisionBatchId} onValueChange={(v) => setProvisionBatchId(v)}>
                    <SelectTrigger className="w-full border-admin-border bg-admin-surface">
                      <SelectValue placeholder="Choisir un lot d'import" />
                    </SelectTrigger>
                    <SelectContent>
                      {batches.map((b) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name} — {b.claimedCount}/{b.createdCount} réclamés
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <label className="flex cursor-pointer items-center justify-between gap-3">
                  <span>
                    <span className="text-sm font-medium text-admin-text-secondary">
                      Contacts externes (sans compte)
                    </span>
                    <span className="ml-2 text-xs text-admin-text-muted">
                      {contactStats
                        ? `${contactStats.active.toLocaleString("fr-FR")} actif(s) — e-mail uniquement`
                        : "importez-les depuis la page Imports"}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={includeContacts}
                    onChange={(e) => setIncludeContacts(e.target.checked)}
                    className="h-4 w-4 accent-[#472cb3]"
                  />
                </label>
              </div>
            </div>

            {roleCriteria && (
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium text-admin-text-secondary">
                  {roleCriteria.label}
                  <span className="ml-2 font-normal text-admin-text-muted">
                    (aucune sélection = tous)
                  </span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {roleCriteria.options!.map((opt) => {
                    const active = roles.includes(opt.value);
                    return (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => toggleRole(opt.value)}
                        className={cn(
                          "rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                          active
                            ? "border-admin-primary bg-admin-primary/10 text-admin-primary"
                            : "border-admin-border bg-admin-surface-elevated text-admin-text-secondary hover:bg-admin-surface-hover",
                        )}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {criteria
              .filter(
                (c) => c.type === "boolean" && c.key !== "roles" && c.key !== "excludeUserIds",
              )
              .map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-admin-text-secondary">{c.label}</p>
                    {c.description && (
                      <p className="text-xs text-admin-text-muted">{c.description}</p>
                    )}
                  </div>
                  <Select
                    value={bools[c.key] === undefined ? "all" : bools[c.key] ? "yes" : "no"}
                    onValueChange={(v) =>
                      setBools((prev) => ({
                        ...prev,
                        [c.key]: v === "all" ? undefined : v === "yes",
                      }))
                    }
                  >
                    <SelectTrigger className="w-36 border-admin-border bg-admin-surface-elevated">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous</SelectItem>
                      <SelectItem value="yes">Oui</SelectItem>
                      <SelectItem value="no">Non</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}

            {criteria
              .filter((c) => c.type === "number" && c.key !== "roles" && c.key !== "excludeUserIds")
              .map((c) => (
                <div key={c.key} className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-admin-text-secondary">{c.label}</p>
                    {c.description && (
                      <p className="text-xs text-admin-text-muted">{c.description}</p>
                    )}
                  </div>
                  <Input
                    type="number"
                    min={0}
                    value={nums[c.key] ?? ""}
                    onChange={(e) => setNums((prev) => ({ ...prev, [c.key]: e.target.value }))}
                    placeholder="—"
                    className="w-32 border-admin-border bg-admin-surface-elevated"
                  />
                </div>
              ))}

            {excludeCriteria && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-admin-text-secondary">
                  {excludeCriteria.label}
                </label>
                <textarea
                  value={excludeRaw}
                  onChange={(e) => setExcludeRaw(e.target.value)}
                  placeholder="IDs séparés par virgule ou retour à la ligne"
                  rows={2}
                  className="w-full rounded-md border border-admin-border bg-admin-surface-elevated p-2 text-xs text-admin-text-primary outline-none placeholder:text-admin-text-muted focus:ring-1 focus:ring-admin-primary"
                />
              </div>
            )}

            {/* Compteur d'audience live */}
            <div className="flex items-center justify-between gap-3 rounded-xl-admin border border-admin-border bg-admin-surface-elevated p-3">
              <div>
                <p className="text-sm font-medium text-admin-text-primary">
                  {!includePlatformUsers
                    ? "Segment plateforme désactivé"
                    : segment
                      ? countQuery.isFetching
                        ? "Calcul de l'audience…"
                        : `${(countQuery.data ?? 0).toLocaleString("fr-FR")} utilisateur(s) ciblé(s)`
                      : "Aucun filtre — tout utilisateur actif sera ciblé"}
                </p>
                <p className="mt-0.5 text-xs text-admin-text-muted">
                  {selectedBatch && (
                    <>
                      + {selectedBatch.createdCount.toLocaleString("fr-FR")} comptes migrés (
                      {selectedBatch.claimedCount} réclamés) ·{" "}
                    </>
                  )}
                  {includeContacts && (
                    <>
                      + {(contactStats?.active ?? 0).toLocaleString("fr-FR")} contacts externes
                      ·{" "}
                    </>
                  )}
                  Total estimé :{" "}
                  <strong className="text-admin-text-secondary">
                    {(
                      (includePlatformUsers ? (segment ? (countQuery.data ?? 0) : 0) : 0) +
                      (selectedBatch?.createdCount ?? 0) +
                      (includeContacts ? (contactStats?.active ?? 0) : 0)
                    ).toLocaleString("fr-FR")}
                  </strong>
                </p>
                {previewTotal != null && (
                  <p className="text-xs text-admin-text-muted">
                    Aperçu vérifié : {previewTotal} au total
                  </p>
                )}
              </div>
              <Button variant="outline" size="sm" onClick={runPreview} disabled={preview.isPending}>
                {preview.isPending ? (
                  <CircleNotchIcon size={14} className="animate-spin" />
                ) : (
                  <EyeIcon size={14} />
                )}
                Aperçu
              </Button>
            </div>

            {previewSample && (
              <div className="rounded-lg border border-admin-border p-2">
                {previewSample.length === 0 ? (
                  <p className="p-2 text-center text-xs text-admin-text-muted">
                    Aucun utilisateur ne correspond.
                  </p>
                ) : (
                  previewSample.map((u) => (
                    <p key={u.id} className="px-2 py-1 text-xs text-admin-text-secondary">
                      {u.name} <span className="text-admin-text-muted">· {u.email}</span>
                    </p>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Étape 4 — Canaux & priorité */}
        {step === 3 && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <label className="text-sm font-medium text-admin-text-secondary">
                Canaux de diffusion *
              </label>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(CHANNEL_META) as CampaignChannel[]).map((ch) => {
                  const active = channels.includes(ch);
                  return (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => toggleChannel(ch)}
                      className={cn(
                        "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
                        active
                          ? "border-admin-primary bg-admin-primary/10 text-admin-primary"
                          : "border-admin-border bg-admin-surface-elevated text-admin-text-secondary hover:bg-admin-surface-hover",
                      )}
                    >
                      {CHANNEL_META[ch].icon}
                      {CHANNEL_META[ch].label}
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-admin-text-muted">
                Chaque canal impose son format : in-app (titre + message), push (titre court),
                e-mail (complet).
              </p>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium text-admin-text-secondary">Priorité</p>
                <p className="text-xs text-admin-text-muted">
                  Les campagnes haute priorité sont traitées d'abord.
                </p>
              </div>
              <Select value={priority} onValueChange={(v) => setPriority(v as CampaignPriority)}>
                <SelectTrigger className="w-40 border-admin-border bg-admin-surface-elevated">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(PRIORITY_META) as CampaignPriority[]).map((p) => (
                    <SelectItem key={p} value={p}>
                      {PRIORITY_META[p].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Étape 5 — Planification */}
        {step === 4 && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  {
                    value: "now",
                    label: "Envoyer maintenant",
                    icon: <PaperPlaneRightIcon size={16} />,
                  },
                  { value: "schedule", label: "Programmer", icon: <ClockIcon size={16} /> },
                ] as const
              ).map((mode) => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setSendMode(mode.value)}
                  className={cn(
                    "flex items-center justify-center gap-2 rounded-xl-admin border p-3 text-sm font-medium transition-colors",
                    sendMode === mode.value
                      ? "border-admin-primary bg-admin-primary/10 text-admin-primary"
                      : "border-admin-border bg-admin-surface-elevated text-admin-text-secondary hover:bg-admin-surface-hover",
                  )}
                >
                  {mode.icon}
                  {mode.label}
                </button>
              ))}
            </div>

            {sendMode === "schedule" && (
              <div className="flex flex-col gap-1.5">
                <label className="text-sm font-medium text-admin-text-secondary">
                  Date et heure d'envoi
                </label>
                <Input
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => setScheduledAt(e.target.value)}
                  className="border-admin-border bg-admin-surface-elevated"
                />
                {scheduledAt && new Date(scheduledAt).getTime() <= Date.now() && (
                  <p className="text-xs text-red-600">La date doit être dans le futur.</p>
                )}
              </div>
            )}

            {/* Récapitulatif */}
            <div className="rounded-xl-admin border border-admin-border bg-admin-surface-elevated p-4 text-sm">
              <p className="mb-2 font-semibold text-admin-text-primary">Récapitulatif</p>
              <ul className="flex flex-col gap-1 text-admin-text-secondary">
                <li>
                  <span className="text-admin-text-muted">Titre :</span> {title}
                </li>
                <li>
                  <span className="text-admin-text-muted">Canaux :</span>{" "}
                  {channels.map((c) => CHANNEL_META[c].label).join(", ") || "—"}
                </li>
                <li>
                  <span className="text-admin-text-muted">Priorité :</span>{" "}
                  {PRIORITY_META[priority].label}
                </li>
                <li>
                  <span className="text-admin-text-muted">Audience :</span>{" "}
                  {includePlatformUsers
                    ? segment
                      ? `${(countQuery.data ?? 0).toLocaleString("fr-FR")} ciblé(s)`
                      : "tous les actifs"
                    : "segment désactivé"}
                  {selectedBatch && (
                    <> + {selectedBatch.createdCount.toLocaleString("fr-FR")} migrés</>
                  )}
                  {includeContacts && (
                    <> + {(contactStats?.active ?? 0).toLocaleString("fr-FR")} externes</>
                  )}
                </li>
                <li>
                  <span className="text-admin-text-muted">Envoi :</span>{" "}
                  {sendMode === "now"
                    ? "immédiat"
                    : `le ${scheduledAt ? fmtDateTime(new Date(scheduledAt).toISOString()) : "—"}`}
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* Footer — navigation */}
        <Separator className="bg-admin-border" />
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={onClose} className="text-admin-text-muted">
            Annuler
          </Button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <Button variant="outline" onClick={() => setStep((s) => s - 1)}>
                Retour
              </Button>
            )}
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep((s) => s + 1)} disabled={!stepValid}>
                Continuer
              </Button>
            ) : (
              <Button onClick={submit} disabled={!stepValid || create.isPending}>
                {create.isPending ? (
                  <CircleNotchIcon size={14} className="animate-spin" />
                ) : (
                  <PaperPlaneRightIcon size={14} />
                )}
                Créer la campagne
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/** ── Détail + monitoring (progression live, destinataires, actions) ─────── */

const CampaignDetailDialog = ({
  campaignId,
  onClose,
}: {
  campaignId: string;
  onClose: () => void;
}) => {
  const { campaign, loading } = useCampaign(campaignId);
  const { recipients } = useCampaignRecipients(campaignId);
  const { deliveries } = useContactDeliveries(campaignId);
  const send = useSendCampaign();
  const cancel = useCancelCampaign();
  const remove = useDeleteCampaign();
  const sendTest = useSendTestCampaign();
  const [armed, setArmed] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  if (loading || !campaign) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="border-admin-border bg-admin-surface">
          <p className="py-8 text-center text-admin-text-muted">Chargement de la campagne…</p>
        </DialogContent>
      </Dialog>
    );
  }

  const meta = STATUS_META[campaign.status];
  const total = campaign.totalAudience ?? 0;
  const processed = campaign.processedCount ?? 0;
  const pct =
    total > 0
      ? Math.min(100, Math.round((processed / total) * 100))
      : campaign.status === "completed"
        ? 100
        : 0;

  const doSend = async () => {
    if (!armed) {
      setArmed(true);
      return;
    }
    try {
      await send.mutateAsync(campaign.id);
      toast.success("Envoi démarré — suivi en direct");
      setArmed(false);
    } catch {
      toast.error("Échec du lancement de l'envoi");
      setArmed(false);
    }
  };

  const doCancel = async () => {
    if (!window.confirm("Annuler cette campagne ? Les envois restants seront abandonnés.")) return;
    try {
      await cancel.mutateAsync(campaign.id);
      toast.success("Campagne annulée");
    } catch {
      toast.error("Échec de l'annulation");
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Supprimer définitivement cette campagne ?")) return;
    try {
      await remove.mutateAsync(campaign.id);
      toast.success("Campagne supprimée");
      onClose();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  const doTest = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testEmail)) {
      toast.error("Adresse e-mail invalide");
      return;
    }
    try {
      const result = await sendTest.mutateAsync({ id: campaign.id, email: testEmail });
      if (result.delivered) {
        toast.success(`Test envoyé à ${testEmail}`);
        setTestEmail("");
      } else {
        toast.error(result.reason ?? "Le test n'a pas été délivré");
      }
    } catch {
      toast.error("Échec de l'envoi du test");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto border-admin-border bg-admin-surface">
        <DialogHeader>
          <DialogTitle className="text-admin-text-primary">{campaign.title}</DialogTitle>
          <DialogDescription className="flex flex-wrap items-center gap-2 pt-1">
            <Badge className={cn("border-0", meta.className)}>{meta.label}</Badge>
            <Badge className={cn("border-0", PRIORITY_META[campaign.priority].className)}>
              Priorité {PRIORITY_META[campaign.priority].label.toLowerCase()}
            </Badge>
            {campaign.channels.map((ch) => (
              <Badge
                key={ch}
                className="gap-1 border-0 bg-admin-surface-hover text-xs text-admin-text-secondary"
              >
                {CHANNEL_META[ch].icon}
                {CHANNEL_META[ch].label}
              </Badge>
            ))}
          </DialogDescription>
        </DialogHeader>

        {campaign.body && (
          <p className="rounded-lg border border-admin-border bg-admin-surface-elevated p-3 text-sm text-admin-text-secondary">
            {campaign.body}
          </p>
        )}
        {campaign.link && (
          <p className="text-xs text-admin-text-muted">
            Lien : <span className="font-mono text-admin-text-secondary">{campaign.link}</span>
          </p>
        )}
        {campaign.segment && Object.keys(campaign.segment).length > 0 && (
          <pre className="max-h-24 overflow-auto rounded-lg border border-admin-border bg-admin-surface-elevated p-2 font-mono text-xs text-admin-text-secondary">
            {JSON.stringify(campaign.segment)}
          </pre>
        )}
        {campaign.status === "failed" && campaign.lastError && (
          <p className="flex items-center gap-2 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
            <WarningCircleIcon size={16} /> {campaign.lastError}
          </p>
        )}

        {/* Progression live */}
        {(campaign.status === "sending" || campaign.status === "completed" || total > 0) && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between text-sm">
              <span className="font-medium text-admin-text-secondary">Progression</span>
              <span className="text-admin-text-muted">
                {processed}/{total} · {pct}%
                {campaign.status === "sending" && (
                  <CircleNotchIcon size={12} className="ml-1.5 inline animate-spin" />
                )}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-full bg-admin-surface-elevated">
              <div
                className={cn(
                  "h-full rounded-full transition-all",
                  campaign.status === "failed"
                    ? "bg-red-500"
                    : campaign.status === "completed"
                      ? "bg-emerald-500"
                      : "bg-amber-500",
                )}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="flex flex-wrap gap-x-4 text-xs text-admin-text-muted">
              {campaign.scheduledAt && (
                <span>Programmée : {fmtDateTime(campaign.scheduledAt)}</span>
              )}
              {campaign.sentAt && <span>Envoi démarré : {fmtDateTime(campaign.sentAt)}</span>}
              {campaign.completedAt && <span>Terminée : {fmtDateTime(campaign.completedAt)}</span>}
              {campaign.cancelledAt && <span>Annulée : {fmtDateTime(campaign.cancelledAt)}</span>}
            </div>
          </div>
        )}

        {/* Envoi test (brouillon uniquement) */}
        {campaign.status === "draft" && (
          <div className="flex items-center gap-2">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="E-mail de test…"
              className="border-admin-border bg-admin-surface-elevated"
            />
            <Button variant="outline" size="sm" onClick={doTest} disabled={sendTest.isPending}>
              {sendTest.isPending ? (
                <CircleNotchIcon size={14} className="animate-spin" />
              ) : (
                <PaperPlaneRightIcon size={14} />
              )}
              Tester
            </Button>
          </div>
        )}

        {/* Destinataires */}
        {recipients.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-admin-text-secondary">
              Destinataires ({recipients.length})
            </p>
            <div className="max-h-52 divide-y divide-admin-border overflow-y-auto rounded-lg border border-admin-border">
              {recipients.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-admin-text-primary">{r.name ?? r.userId}</p>
                    {r.email && <p className="truncate text-xs text-admin-text-muted">{r.email}</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    {r.error ? (
                      <span
                        className="inline-flex max-w-40 items-center gap-1 truncate text-xs text-red-600"
                        title={r.error}
                      >
                        <WarningCircleIcon size={12} /> {r.error}
                      </span>
                    ) : r.inAppSent ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <CheckCircleIcon size={12} /> Envoyé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-admin-text-muted">
                        <ClockIcon size={12} /> En attente
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Destinataires externes (contacts sans compte — e-mail) */}
        {deliveries.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-sm font-medium text-admin-text-secondary">
              Contacts externes ({deliveries.length}) — e-mail direct
            </p>
            <div className="max-h-52 divide-y divide-admin-border overflow-y-auto rounded-lg border border-admin-border">
              {deliveries.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <p className="truncate text-sm text-admin-text-primary">{d.name ?? d.email}</p>
                    {d.name && <p className="truncate text-xs text-admin-text-muted">{d.email}</p>}
                  </div>
                  <div className="shrink-0 text-right">
                    {d.error ? (
                      <span
                        className="inline-flex max-w-40 items-center gap-1 truncate text-xs text-red-600"
                        title={d.error}
                      >
                        <WarningCircleIcon size={12} /> Échec
                      </span>
                    ) : d.emailSent ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                        <CheckCircleIcon size={12} /> Envoyé
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-admin-text-muted">
                        <ClockIcon size={12} /> En attente
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <Separator className="bg-admin-border" />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-xs text-admin-text-muted">
            Créée le {fmtDateTime(campaign.createdAt)}
          </span>
          <div className="flex items-center gap-2">
            {campaign.status === "draft" && (
              <>
                <Button
                  variant="outline"
                  onClick={doSend}
                  disabled={send.isPending}
                  className={cn(armed && "border-amber-500/50 text-amber-600")}
                >
                  {send.isPending ? (
                    <CircleNotchIcon size={14} className="animate-spin" />
                  ) : (
                    <PaperPlaneRightIcon size={14} />
                  )}
                  {armed ? "Confirmer l'envoi" : "Envoyer maintenant"}
                </Button>
                <Button
                  variant="error"
                  size="icon"
                  onClick={doDelete}
                  disabled={remove.isPending}
                  title="Supprimer"
                >
                  <TrashIcon size={14} />
                </Button>
              </>
            )}
            {(campaign.status === "scheduled" || campaign.status === "sending") && (
              <Button variant="outline" onClick={doCancel} disabled={cancel.isPending}>
                <ProhibitIcon size={14} /> Annuler la campagne
              </Button>
            )}
            {(campaign.status === "cancelled" || campaign.status === "failed") && (
              <Button
                variant="error"
                size="icon"
                onClick={doDelete}
                disabled={remove.isPending}
                title="Supprimer"
              >
                <TrashIcon size={14} />
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
